import { formatFleetKw, type FleetSolar } from '@gb-ticket/shared-types';
import {
  ArrowUpFromLineIcon,
  BatteryChargingIcon,
  BatteryFullIcon,
  BatteryLowIcon,
  BatteryMediumIcon,
  FactoryIcon,
  SunIcon,
  UtilityPoleIcon,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import { useFleetSolar } from '@/products/fleet/features/use-fleet';

function berlinTime(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function ForecastLines({ solar }: { solar: FleetSolar }) {
  if (solar.forecastState !== 'ok') {
    return <p className="text-sm text-muted-foreground">Prognose nicht erreichbar</p>;
  }
  return (
    <div className="space-y-1 text-sm text-muted-foreground">
      {solar.sunSentence ? <p>{solar.sunSentence}</p> : null}
      {solar.surplusSentence ? <p>{solar.surplusSentence}</p> : null}
      {solar.sourceLabel ? <p className="text-xs">{solar.sourceLabel}</p> : null}
    </div>
  );
}

function batteryDetail(solar: FleetSolar): string {
  if (solar.batterySoc == null) return 'Keine Daten';
  if (solar.batteryFlow === 'charge' && solar.batteryKw != null) {
    return `lädt ${formatFleetKw(solar.batteryKw)} kW`;
  }
  if (solar.batteryFlow === 'discharge' && solar.batteryKw != null) {
    return `entlädt ${formatFleetKw(solar.batteryKw)} kW`;
  }
  if (solar.batterySoc >= 95) return 'voll';
  return 'steht';
}

function batteryTone(soc: number | null): 'green' | 'amber' | 'red' | 'muted' {
  if (soc == null) return 'muted';
  if (soc >= 80) return 'green';
  if (soc >= 40) return 'amber';
  return 'red';
}

function batteryIcon(solar: FleetSolar): LucideIcon {
  if (solar.batteryFlow === 'charge') return BatteryChargingIcon;
  if (solar.batterySoc == null) return BatteryMediumIcon;
  if (solar.batterySoc >= 80) return BatteryFullIcon;
  if (solar.batterySoc >= 40) return BatteryMediumIcon;
  return BatteryLowIcon;
}

function Figure({
  label,
  value,
  detail,
  tone,
  icon: Icon,
  fill,
}: {
  label: string;
  value: string;
  detail: string;
  tone: 'green' | 'amber' | 'red' | 'blue' | 'muted';
  icon: LucideIcon;
  fill?: number | null;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border px-3 py-3',
        tone === 'green' &&
          'border-green-200 bg-green-50 text-green-950 dark:border-green-900 dark:bg-green-950 dark:text-green-50',
        tone === 'amber' &&
          'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50',
        tone === 'red' &&
          'border-red-200 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950 dark:text-red-50',
        tone === 'blue' &&
          'border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-50',
        tone === 'muted' && 'border-border bg-muted/40 text-foreground',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium tracking-wide uppercase">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
        <p className="mt-1 text-sm">{detail}</p>
        {fill != null ? (
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/10 dark:bg-white/15">
            <div className="h-full rounded-full bg-current" style={{ width: `${fill}%` }} />
          </div>
        ) : null}
      </div>
      <Icon className="size-11 shrink-0" aria-hidden />
    </div>
  );
}

export function FleetSolarCard() {
  const solar = useFleetSolar();
  if (solar.isPending) {
    return <p className="text-sm text-muted-foreground">Solaranlage wird geladen …</p>;
  }
  const data = solar.isError ? null : solar.data;
  if (!data || data.state === 'unavailable') {
    return (
      <section className={fleetPanelClassName}>
        <p className="font-semibold">Solaranlage nicht erreichbar</p>
        {data ? (
          <div className="mt-2">
            <ForecastLines solar={data} />
          </div>
        ) : null}
      </section>
    );
  }
  const gridText = data.gridKw === 0 ? '0' : formatFleetKw(data.gridKw ?? 0);
  return (
    <section className={`${fleetPanelClassName} space-y-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p
          className={cn(
            'text-xl font-semibold',
            data.state === 'export'
              ? 'text-green-700 dark:text-green-300'
              : 'text-red-700 dark:text-red-300',
          )}
        >
          {data.advice}
        </p>
        {data.stale && data.observedAt ? (
          <p className="text-xs text-muted-foreground">Stand {berlinTime(data.observedAt)}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        <Figure
          label="Erzeugung"
          value={`${formatFleetKw(data.pvKw ?? 0)} kW`}
          detail="Photovoltaik"
          tone="amber"
          icon={SunIcon}
        />
        <Figure
          label={data.gridLabel ?? 'Netz'}
          value={`${gridText} kW`}
          detail={data.state === 'export' ? 'ins Netz' : 'aus dem Netz'}
          tone={data.state === 'export' ? 'green' : 'red'}
          icon={data.state === 'export' ? ArrowUpFromLineIcon : UtilityPoleIcon}
        />
        <Figure
          label="Akku"
          value={data.batterySoc == null ? '—' : `${data.batterySoc} %`}
          detail={data.batterySoc == null ? 'Keine Daten' : `${batteryDetail(data)}`}
          tone={batteryTone(data.batterySoc)}
          icon={batteryIcon(data)}
          fill={data.batterySoc}
        />
        <Figure
          label="Verbrauch"
          value={data.loadKw == null ? '—' : `${formatFleetKw(data.loadKw)} kW`}
          detail="Gebäude"
          tone="blue"
          icon={FactoryIcon}
        />
      </div>
      <ForecastLines solar={data} />
    </section>
  );
}
